import React, { useState } from "react";
import payment from "../../data/payment.json";
import { useLanguage } from "../../i18n/LanguageContext";
import { fillTemplate } from "../../Components/RichText";

function AdmissionForm({ batch, course, setToNextStep }) {
  const { lang, content, formatNumber } = useLanguage();
  const t = content.ui.form;
  const p = content.ui.payment;
  const [formData, setFormData] = useState({
    fullName: "",
    mobileNo: "",
    studyingInAcademy: "",
    heardFrom: "",
    referrerName: "",
    paymentMode: "",
    trxId: "",
    paymentAmount: "",
    screenshot: null,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, screenshot: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch('https://quran-learning-platform-server.onrender.com/api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        console.log("Form submitted successfully");
      } else {
        console.error("Form submission failed:");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
    setToNextStep(3);
  };

  return (
    <div className="max-w-6xl mx-auto md:p-8 md:rounded-xl text-left">
      <h1 className="text-2xl font-serif font-bold text-center mb-8 text-primary">{t.heading}</h1>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-12">
        
        {/* Left Column */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-textDark mb-2">{t.fullName}</label>
            <input
              required
              type="text"
              name="fullName"
              autoFocus={true}
              value={formData.fullName}
              onChange={handleChange}
              className="w-full border-b-2 border-secondary bg-transparent focus:outline-none focus:border-primary text-sm py-2 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDark mb-2">{t.mobileNo}</label>
            <input
              required
              type="text"
              name="mobileNo"
              value={formData.mobileNo}
              onChange={handleChange}
              className="w-full border-b-2 border-secondary bg-transparent focus:outline-none focus:border-primary text-sm py-2 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDark mb-2">{t.otherAcademy}</label>
            <input
              required
              type="text"
              name="studyingInAcademy"
              value={formData.studyingInAcademy}
              onChange={handleChange}
              className="w-full border-b-2 border-secondary bg-transparent focus:outline-none focus:border-primary text-sm py-2 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDark mb-2">{t.heardFrom} <span className="text-red-500">*</span></label>
            <div className="flex flex-col space-y-3 mt-2">
              {Object.keys(t.heardOptions).map((option) => (
                // Submitted values stay in English; only the labels are translated
                <label key={option} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="heardFrom"
                    value={option}
                    checked={formData.heardFrom === option}
                    onChange={handleChange}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm text-textDark">{t.heardOptions[option]}</span>
                </label>
              ))}
            </div>
          </div>
          
          {formData.heardFrom === "From other student" && (
            <div>
              <label className="block text-sm font-medium text-textDark mb-2">{t.referrer}</label>
              <input
                type="text"
                name="referrerName"
                value={formData.referrerName}
                onChange={handleChange}
                className="w-full border-b-2 border-secondary bg-transparent focus:outline-none focus:border-primary text-sm py-2 transition-colors"
              />
            </div>
          )}

          {/* Payment Details Text */}
          <div className="p-4 bg-secondary/20 rounded-lg mt-8 border border-secondary/30">
            <p className="text-sm text-textDark">
              {fillTemplate(t.monthlyPayment, {
                course: <span className="font-semibold text-primary">{course.name[lang]}</span>,
                price: <span className="font-bold text-primary">{formatNumber(course.pricing)} {p.currency}</span>,
              })}
            </p>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Account Details */}
          <div className="p-6 bg-white rounded-xl border border-secondary/20 shadow-sm">
            <h2 className="text-xl font-serif font-bold text-primary mb-4">{t.accountDetails}</h2>
            <div className="space-y-3 text-sm text-textDark">
              <div>
                <p className="font-semibold">{p.bank}</p>
                <p>{p.bankName}</p>
                <p>{p.accountNo} <span className="font-mono">{payment.bankAccountNumber}</span></p>
                <p>{p.accountHolder} {payment.bankAccountHolder}</p>
              </div>
              <div className="pt-3 border-t border-secondary/20">
                <p className="font-semibold">{p.bkash}:</p>
                <p className="font-mono">{payment.bkashNumber}</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-textDark mb-2">{t.paymentMode} <span className="text-red-500">*</span></label>
            <div className="flex flex-col space-y-3 mt-2">
              {Object.keys(t.paymentOptions).map((option) => (
                <label key={option} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    required
                    type="radio"
                    name="paymentMode"
                    value={option}
                    checked={formData.paymentMode === option}
                    onChange={handleChange}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm text-textDark">{t.paymentOptions[option]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Conditional Rendering based on paymentMode */}
          {formData.paymentMode === "Bank" && (
            <div>
              <label className="block text-sm font-medium text-textDark mb-2">{t.uploadScreenshot}</label>
              <input
                required
                type="file"
                name="screenshot"
                onChange={handleFileChange}
                className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-white file:font-medium hover:file:bg-primaryLight file:cursor-pointer"
              />
            </div>
          )}
          {(formData.paymentMode === "Bkash" || formData.paymentMode === "Nagad") && (
            <div>
              <label className="block text-sm font-medium text-textDark mb-2">{t.trxId}</label>
              <input
                required
                type="text"
                name="trxId"
                value={formData.trxId}
                onChange={handleChange}
                className="w-full border-b-2 border-secondary bg-transparent focus:outline-none focus:border-primary text-sm py-2 transition-colors"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-textDark mb-2">{t.paymentAmount}</label>
            <input
              required
              type="text"
              name="paymentAmount"
              value={formData.paymentAmount}
              onChange={handleChange}
              className="w-full border-b-2 border-secondary bg-transparent focus:outline-none focus:border-primary text-sm py-2 transition-colors"
            />
          </div>
          <button 
            type="submit"
            className="w-full mt-6 px-8 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primaryLight transition-colors shadow-md"
          >
            {t.submit}
          </button>
        </div>
        
      </form>
    </div>
  );
}

export default AdmissionForm;
